import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format, differenceInDays } from 'date-fns';
import {
  Lock,
  Zap,
  Sparkles,
  Gift,
  Ticket,
  CheckCircle2,
  AlertCircle,
  Calendar,
  Clock,
  ArrowUpRight,
  Flame,
  ExternalLink,
  Loader2,
} from 'lucide-react';
import { subscriptionService } from '../../services/subscriptionService';
import { useSubscription, SUBSCRIPTION_QUERY_KEY } from '../../hooks/useSubscription';
import { useLanguage } from '../../features/language/useLanguage';
import { useToast } from '../../components/common/Toast';
import { LYNK_PURCHASE_URL } from '../../constants/lynk';

export default function SubscriptionSettings() {
  const { t } = useLanguage();
  const sub = useSubscription();
  const qc = useQueryClient();
  const { showToast } = useToast();
  const [code, setCode] = useState('');
  const [status, setStatus] = useState<{ kind: 'idle' | 'success' | 'error'; message?: string }>({
    kind: 'idle',
  });

  const history = useQuery({
    queryKey: ['subscription', 'redemptions'],
    queryFn: subscriptionService.listMyRedemptions,
  });

  const redeem = useMutation({
    mutationFn: (c: string) => subscriptionService.redeem(c),
    onSuccess: (res) => {
      const msg = t('subscription.redeem.success', { name: res.voucher_name, months: res.months_added });
      setStatus({
        kind: 'success',
        message: msg,
      });
      showToast(msg, 'success');
      setCode('');
      qc.invalidateQueries({ queryKey: SUBSCRIPTION_QUERY_KEY });
      qc.invalidateQueries({ queryKey: ['subscription', 'redemptions'] });
    },
    onError: (err: unknown) => {
      const apiErr = err as { response?: { data?: { message?: string } } };
      const m = apiErr?.response?.data?.message ?? t('subscription.redeem.error_generic');
      setStatus({ kind: 'error', message: m });
      showToast(m, 'error');
    },
  });

  const tier = sub.tier;
  const machineTypeQuota = sub.machineTypeQuota;
  const daysRemaining = sub.expiresAt ? differenceInDays(sub.expiresAt, new Date()) : null;
  const displayTier: 'free' | 'basic' | 'plus' =
    tier === 'plus' ? 'plus' : tier === 'basic' ? 'basic' : 'free';
  const isPaidTier = displayTier !== 'free';
  const isExpiringSoon = isPaidTier && daysRemaining !== null && daysRemaining <= 7;
  const items = history.data ?? [];

  // The hero CTA's destination tier — drives the button label and visibility.
  // 'free' → upgrade to basic, 'basic' → upgrade to plus, 'plus' → no CTA.
  const upgradeTarget: 'basic' | 'plus' | null =
    tier === 'free' ? 'basic' : tier === 'basic' ? 'plus' : null;
  const upgradeLabel = upgradeTarget === 'plus'
    ? t('subscription.buy.upgrade_to_plus')
    : t('subscription.buy.upgrade_to_basic');

  const heroTheme: Record<
    typeof displayTier,
    {
      container: string;
      glow: string | null;
      iconWrap: string;
      badge: string;
      calendar: string;
      icon: typeof Lock;
    }
  > = {
    free: {
      container: 'border-gray-200 bg-white',
      glow: null,
      iconWrap: 'bg-gray-100 text-gray-500',
      badge: 'bg-gray-200 text-gray-700',
      calendar: 'text-gray-500',
      icon: Lock,
    },
    basic: {
      container: 'border-sky-200 bg-gradient-to-br from-sky-50 via-white to-cyan-50',
      glow: 'bg-sky-200/40',
      iconWrap: 'bg-sky-600 text-white shadow-md shadow-sky-600/30',
      badge: 'bg-sky-600 text-white',
      calendar: 'text-sky-600',
      icon: Zap,
    },
    plus: {
      container: 'border-amber-200 bg-gradient-to-br from-amber-50 via-white to-orange-50',
      glow: 'bg-amber-200/40',
      iconWrap: 'bg-gradient-to-br from-amber-500 to-orange-500 text-white shadow-md shadow-amber-500/30',
      badge: 'bg-amber-500 text-white',
      calendar: 'text-amber-600',
      icon: Sparkles,
    },
  };
  const theme = heroTheme[displayTier];
  const TierIcon = theme.icon;

  // PRD 76: per-cycle roast quota usage card.
  const quota = sub.roastQuota;
  const usagePct = quota && quota.limitKg > 0
    ? Math.min(100, (quota.usedKg / quota.limitKg) * 100)
    : 0;
  const usageColor = usagePct >= 90
    ? 'bg-red-500'
    : usagePct >= 70
      ? 'bg-amber-500'
      : 'bg-emerald-500';

  return (
    <div className="space-y-6" data-test-id="subscription-settings-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">{t('settings.subscription')}</h1>
          <p className="text-sm text-gray-500 mt-1">{t('subscription.page_subtitle')}</p>
        </div>
      </div>

      {/* Hero — Current Tier */}
      <div
        className={`relative overflow-hidden rounded-xl border p-6 shadow-sm ${theme.container}`}
        data-test-id="subscription-hero-card"
        data-tier={displayTier}
      >
        {theme.glow && (
          <div
            className={`pointer-events-none absolute -top-16 -right-16 h-48 w-48 rounded-full blur-3xl ${theme.glow}`}
          />
        )}
        <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div className="flex items-start gap-4 min-w-0">
            <div
              className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-xl ${theme.iconWrap}`}
              data-test-id="hero-tier-icon-wrap"
            >
              <TierIcon className="h-7 w-7" data-test-id="hero-tier-icon" />
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold uppercase tracking-wider ${theme.badge}`}
                  data-test-id="hero-tier-badge"
                >
                  {t(`subscription.tier.${displayTier}`)}
                </span>
                {isExpiringSoon && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 px-2.5 py-1 text-xs font-medium">
                    <AlertCircle className="h-3 w-3" />
                    {t('subscription.banner.warning', { days: daysRemaining })}
                  </span>
                )}
              </div>
              <h2 className="mt-2 text-xl font-semibold text-gray-900">
                {isPaidTier ? t('subscription.hero_pro_title') : t('subscription.hero_free_title')}
              </h2>
              {/* <p className="mt-1 text-sm text-gray-600 max-w-md">
                {isPaid ? t('subscription.hero_pro_subtitle') : t('subscription.hero_free_subtitle')}
              </p> */}
            </div>
          </div>
          <div className="flex flex-col gap-3 sm:items-end">
            {isPaidTier && sub.expiresAt && (
              <>
                <div className="text-xs uppercase tracking-wider text-gray-500 font-medium">
                  {t('subscription.expires_on')}
                </div>
                <div className="flex items-center gap-2 text-gray-900">
                  <Calendar className={`h-4 w-4 ${theme.calendar}`} />
                  <span className="text-lg font-semibold tabular-nums">
                    {format(sub.expiresAt, 'PPP')}
                  </span>
                </div>
                {daysRemaining !== null && daysRemaining > 0 && (
                  <div className="flex items-center gap-1.5 text-xs text-gray-500">
                    <Clock className="h-3 w-3" />
                    {t('subscription.days_remaining', { days: daysRemaining })}
                  </div>
                )}
              </>
            )}
            {upgradeTarget && (
              <a
                href={LYNK_PURCHASE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-md bg-primary-600 hover:bg-primary-700 text-white font-medium text-sm shadow-sm transition-colors"
                data-test-id="hero-upgrade-cta"
                data-upgrade-target={upgradeTarget}
              >
                {upgradeTarget === 'plus' ? <Sparkles className="h-4 w-4" /> : <Zap className="h-4 w-4" />}
                {upgradeLabel}
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Roasting usage this month — PRD 76 */}
      {quota && (
        <div
          className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm"
          data-test-id="subscription-roast-usage-card"
        >
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
                <Flame className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-gray-900">
                  {t('subscription.usage.title')}
                </h2>
                <p className="text-sm text-gray-500 mt-0.5">
                  {t('subscription.usage.cycleLabel', {
                    start: format(quota.cycleStart, 'PP'),
                    end:   format(new Date(quota.cycleEnd.getTime() - 1), 'PP'),
                    nextStart: format(quota.cycleEnd, 'PP'),
                  })}
                </p>
              </div>
            </div>
            <span className="text-sm font-semibold text-gray-900 tabular-nums" data-test-id="usage-numbers">
              {t('subscription.usage.usedOf', { used: quota.usedKg, limit: quota.limitKg })}
            </span>
          </div>
          <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className={`h-full ${usageColor} transition-all`}
              style={{ width: `${usagePct}%` }}
              data-test-id="usage-progress-bar"
            />
          </div>
          <div className="mt-3 flex items-center justify-between">
            <p className="text-xs text-gray-500" data-test-id="usage-remaining">
              {t('subscription.usage.remaining', { remaining: quota.remainingKg })}
            </p>
            {!sub.isPlus && (
              <a
                href={LYNK_PURCHASE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 hover:text-amber-800"
                data-test-id="usage-upgrade-cta"
              >
                {t('subscription.usage.upgradeForMore')}
                <ArrowUpRight className="h-3 w-3" />
              </a>
            )}
          </div>
        </div>
      )}

      {/* Machine type quota — PRD 80 */}
      {machineTypeQuota && (
        <section
          className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm"
          data-test-id="settings-machine-type-quota"
        >
          <header className="flex items-center justify-between gap-3 mb-3">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">
                {t('subscription.machineTypeQuota.title')}
              </h3>
              <p className="text-xs text-gray-500 mt-0.5 capitalize">{tier}</p>
            </div>
            {tier !== 'plus' && (
              <a
                href={LYNK_PURCHASE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs font-semibold text-primary-600 hover:text-primary-700"
              >
                {t('subscription.machineTypeQuota.upgradeForMore')}
              </a>
            )}
          </header>
          {tier === 'plus' ? (
            <p className="text-sm font-medium text-gray-700">
              {t('subscription.machineTypeQuota.unlimited')}
            </p>
          ) : (
            <>
              <div className="flex items-baseline justify-between text-sm">
                <span className="font-medium text-gray-900">
                  {t('subscription.machineTypeQuota.usedOf')
                    .replace('{used}', String(machineTypeQuota.used))
                    .replace('{limit}', String(machineTypeQuota.limit))}
                </span>
              </div>
              <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-gray-100">
                <div
                  className={`h-full transition-all ${
                    machineTypeQuota.used >= machineTypeQuota.limit
                      ? 'bg-red-500'
                      : machineTypeQuota.used / machineTypeQuota.limit >= 0.7
                      ? 'bg-amber-500'
                      : 'bg-primary-500'
                  }`}
                  style={{
                    width: `${Math.min(
                      100,
                      (machineTypeQuota.used / machineTypeQuota.limit) * 100
                    )}%`,
                  }}
                />
              </div>
            </>
          )}
        </section>
      )}

      {/* Claim Voucher */}
      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm" id="claim-voucher">
        <div className="flex items-start gap-3 mb-5">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
            <Gift className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-gray-900">{t('subscription.claim_title')}</h2>
            <p className="text-sm text-gray-500 mt-0.5">{t('subscription.claim_subtitle')}</p>
          </div>
        </div>

        <form
          className="flex flex-col sm:flex-row gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (code.trim()) {
              setStatus({ kind: 'idle' });
              redeem.mutate(code.trim());
            }
          }}
        >
          <div className="relative flex-1">
            <Ticket className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder={t('subscription.code_placeholder')}
              className="w-full rounded-md border border-gray-300 bg-white pl-9 pr-3 py-2.5 font-mono uppercase tracking-[0.2em] text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              data-test-id="claim-voucher-code"
            />
          </div>
          <button
            type="submit"
            disabled={redeem.isPending || !code.trim()}
            data-test-id="claim-voucher-submit"
            className="inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-md bg-primary-600 hover:bg-primary-700 text-white font-medium text-sm shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {redeem.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('common.loading')}
              </>
            ) : (
              <>
                {t('subscription.redeem_cta')}
                <ArrowUpRight className="h-4 w-4" />
              </>
            )}
          </button>
        </form>

        {status.kind === 'success' && (
          <div
            className="mt-4 flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2.5"
            data-test-id="redeem-success"
          >
            <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
            <p className="text-sm text-emerald-800">{status.message}</p>
          </div>
        )}
        {status.kind === 'error' && (
          <div
            className="mt-4 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2.5"
            data-test-id="redeem-error"
          >
            <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
            <p className="text-sm text-red-800">{status.message}</p>
          </div>
        )}
      </div>

      {/* History */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-gray-900">{t('subscription.history_title')}</h2>
            <p className="text-sm text-gray-500 mt-0.5">{t('subscription.history_subtitle')}</p>
          </div>
          {items.length > 0 && (
            <span className="text-xs font-medium text-gray-600 bg-gray-100 px-2.5 py-1 rounded-full tabular-nums">
              {items.length}
            </span>
          )}
        </div>
        {items.length === 0 ? (
          <div className="px-6 py-14 text-center">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 mb-3">
              <Ticket className="h-6 w-6 text-gray-400" />
            </div>
            <p className="text-sm text-gray-500">{t('subscription.history_empty')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left">
                <tr>
                  <th className="px-6 py-3 text-xs font-medium uppercase tracking-wider text-gray-500">
                    {t('subscription.history.redeemed_at')}
                  </th>
                  <th className="px-6 py-3 text-xs font-medium uppercase tracking-wider text-gray-500">
                    {t('subscription.history.months_added')}
                  </th>
                  <th className="px-6 py-3 text-xs font-medium uppercase tracking-wider text-gray-500">
                    {t('subscription.history.extended_to')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50/60 transition-colors">
                    <td className="px-6 py-3 text-gray-700 tabular-nums">
                      {format(new Date(r.redeemed_at), 'PPP')}
                    </td>
                    <td className="px-6 py-3">
                      <span className="inline-flex items-center rounded-md bg-primary-50 text-primary-700 px-2 py-0.5 text-xs font-semibold tabular-nums">
                        +{r.months_added} mo
                      </span>
                    </td>
                    <td className="px-6 py-3 text-gray-700 tabular-nums">
                      {format(new Date(r.subscription_extended_to), 'PPP')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
